import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, updateDoc, addDoc, deleteField } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthProvider';
import { useUI } from './UIContext';
import { normalizeKey, normalizeType, normalizeTag } from './utils/normalize'; // or wherever your normalizeKey() lives
import { sanitizeAbcBody } from './components/sanitizeAbcBody';
import myIcon from './assets/logo512.png'; // adjust path
import { cleanAndFillAbc } from './utils/cleanAndFillAbc';
import { downloadAllToLocal } from './utils/downloadAllToLocal';
import dexie from './offlineDB';


const baseGenres = ['Irish', 'Old Time', 'Bluegrass', 'Klezmer'];
const baseTypes = ['Reel', 'Jig', 'Hornpipe', 'Polka', 'Waltz', 'Tune', 'Song'];
const baseTags = ['Awesome', 'Meh', 'To edit'];

const Settings = ({ onBack }) => {
  const { uiSize, setUiSize, inputStyle, textStyle } = useUI();
  const { user } = useAuth();
  const [defaultGenre, setDefaultGenre] = useState('Irish');
  const [defaultType, setDefaultType] = useState('');
  const [defaultKey, setDefaultKey] = useState('');
  const [defaultStatus, setDefaultStatus] = useState('Need to Learn');
  const [theme, setTheme] = useState('light');
  const [customColor, setCustomColor] = useState('#ffffff');

  const [customGenres, setCustomGenres] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);

  const [newGenreInput, setNewGenreInput] = useState('');
  const [newTypeInput, setNewTypeInput] = useState('');

  const [enableAutoSuggest, setEnableAutoSuggest] = useState('yes');

  const [usedGenres, setUsedGenres] = useState([]);
  const [usedTypes, setUsedTypes] = useState([]);
const [customTags, setCustomTags] = useState([]);
const [newTagInput, setNewTagInput] = useState('');
const [usedTags, setUsedTags] = useState([]);


  const [showAbcInSets, setShowAbcInSets] = useState(true);
  const [showLyricsInSets, setShowLyricsInSets] = useState(true);
  const [showAbcInSearch, setShowAbcInSearch] = useState(true);
  const [showLyricsInSearch, setShowLyricsInSearch] = useState(false);


  const [loading, setLoading] = useState(true);
  const [sessionUserId, setSessionUserId] = useState('');
  const [importing, setImporting] = useState(false);





const updatePreference = async (key, value) => {
  if (!user) return;
  const docRef = doc(db, 'users', user.uid, 'preferences', 'settings');

  if (navigator.onLine) {
    await updateDoc(docRef, { [key]: value });
  } else {
    await dexie.pendingWrites.add({
      type: 'update',
      collection: 'preferences',
      docId: 'settings',
      data: { [key]: value }
    });
    await dexie.preferences.put({ id: 'settings', [key]: value });
  }

  if (key === 'theme') localStorage.setItem('theme', value);
};


  const importAllFromSession = async () => {
    setImporting(true);
    if (!sessionUserId) {
      alert("Please enter your Session.org username or ID.");
      return;
    }

    try {
      const res = await fetch(`https://thesession.org/members/${sessionUserId}/tunebook?format=json`);
      const data = await res.json();

      if (!Array.isArray(data.tunes)) {
        alert("No tunes found or invalid ID.");
        return;
      }

      for (const t of data.tunes) {
        const tuneRes = await fetch(`https://thesession.org/tunes/${t.id}?format=json`);
        const tuneData = await tuneRes.json();
        const setting = tuneData.settings?.[0] || {};
   
      const abcRaw = `${setting.abc || ''}`;  // just the ABC body
      const rawHeader = [
        `T:${tuneData.name || ''}`,
        `R:${tuneData.type || ''}`,
        `M:${setting.meter || ''}`,
        `L:${setting.length || ''}`,
        `Q:${setting.tempo || ''}`,
        `Z:${tuneData.transcribed_by || ''}`,
        `K:${setting.key || ''}`
      ].join('\n');

      const rawAbc = `${rawHeader}\n${abcRaw}`;

      const sanitizedAbc = cleanAndFillAbc({
        rawAbc,
        type: tuneData.type,
        key: setting.key
      });


        const newTune = {
          title: tuneData.name || '',
          type: normalizeType(tuneData.type),
          key: normalizeKey(setting.key || ''),
          abc: sanitizedAbc,
          link1: `https://thesession.org/tunes/${t.id}`,
          genre: 'Irish',
          status: '',
          chords: '', lyrics: '', notes: '', link2: '', link3: '', source: '',
          createdAt: new Date().toISOString(),
          sessions: [],
          recordings: [],
          customTags: []
        };


        await addDoc(collection(db, 'users', user.uid, 'tunes'), newTune);
      }

      alert(`Imported ${data.tunes.length} tunes from TheSession.org`);
      setImporting(false);
    } catch (err) {
      console.error(err);
      alert("Error importing tunes.");
      setImporting(false);
    }
  };

useEffect(() => {
  const fetchPreferences = async () => {
    if (!user) return;
    const docRef = doc(db, 'users', user.uid, 'preferences', 'settings');
    const snap = await getDoc(docRef);

    let data = {};
    if (snap.exists()) {
      data = snap.data();
      if (data.defaultGenre) setDefaultGenre(data.defaultGenre);
      if (data.defaultType) setDefaultType(data.defaultType);
      if (data.defaultKey) setDefaultKey(data.defaultKey);
      if (data.defaultStatus) setDefaultStatus(data.defaultStatus);
      if (data.theme) setTheme(data.theme);
      if (data.enableAutoSuggest) setEnableAutoSuggest(data.enableAutoSuggest);
      if (data.showAbcInSets !== undefined) setShowAbcInSets(data.showAbcInSets);
      if (data.showLyricsInSets !== undefined) setShowLyricsInSets(data.showLyricsInSets);
      if (data.showAbcInSearch !== undefined) setShowAbcInSearch(data.showAbcInSearch);
      if (data.showLyricsInSearch !== undefined) setShowLyricsInSearch(data.showLyricsInSearch);
      setCustomGenres(Array.isArray(data.customGenres) ? data.customGenres : baseGenres);
      setCustomTypes(Array.isArray(data.customTypes) ? data.customTypes : baseTypes);
      setCustomTags(Array.isArray(data.customTags ?? data.customRatingLabels) ? (data.customTags ?? data.customRatingLabels) : baseTags);
    } else {
      data = {
        customGenres: baseGenres,
        customTypes: baseTypes,
        customTags: baseTags,
        enableAutoSuggest: 'yes',
        showAbcInSets: true,
        showLyricsInSets: true,
        showAbcInSearch: true,
        showLyricsInSearch: false,
      };
      setCustomGenres(baseGenres);
      setCustomTypes(baseTypes);
      setCustomTags(baseTags);
      setEnableAutoSuggest('yes');
      setShowAbcInSets(true);
      setShowLyricsInSets(true);
      setShowAbcInSearch(true);
      setShowLyricsInSearch(false);
    }

    await dexie.preferences.put({ id: 'settings', ...data }); // ✅ fixes 'data' is not defined
    setLoading(false);
  };

  fetchPreferences();
}, [user]);



useEffect(() => {
  if (!user) return;

  const fetchUsedValues = async () => {
    const tunesRef = collection(db, 'users', user.uid, 'tunes');
    const snapshot = await getDocs(tunesRef);
      await dexie.tunes.bulkPut(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    const genres = new Set();
    const types = new Set();
    const tags = new Set();

    snapshot.docs.forEach(doc => {
      const d = doc.data();
      if (d.genre) genres.add(d.genre);
      if (d.type) types.add(d.type);
      if (Array.isArray(d.customTags)) {
        d.customTags.forEach(tag => tags.add(tag));
      }
    });


    setUsedGenres([...genres]);
    setUsedTypes([...types]);
    setUsedTags([...tags]);

    // 🔄 Normalize and dedupe all tags before saving
    const allTags = new Set([...tags, ...customTags].map(normalizeTag));
    const uniqueTags = [...allTags];

    setCustomTags(uniqueTags);
    updatePreference('customTags', uniqueTags);


  };

  fetchUsedValues();
}, [user]);


useEffect(() => {
  if (theme) {
    const themes = ['theme-light', 'theme-dark', 'theme-sepia', 'theme-blueprint', 'theme-custom'];
    themes.forEach(t => document.body.classList.remove(t));
    document.body.classList.add(`theme-${theme}`);

    if (theme === 'custom') {
      const stored = localStorage.getItem('customBgColor');
      const bg = stored || '#ffffff';
      setCustomColor(bg);

      const yiq = (parseInt(bg.slice(1, 3), 16) * 299 +
                   parseInt(bg.slice(3, 5), 16) * 587 +
                   parseInt(bg.slice(5, 7), 16) * 114) / 1000;
      const textColor = yiq >= 128 ? '#000000' : '#ffffff';

      document.documentElement.style.setProperty('--custom-bg', bg);
      document.documentElement.style.setProperty('--custom-text', textColor);
      document.documentElement.style.setProperty('--custom-button-bg', bg);
      document.documentElement.style.setProperty('--custom-button-text', textColor);
    }
  }
}, [theme]);


  const addGenre = () => {
    const trimmed = newGenreInput.trim();
    if (trimmed && !customGenres.includes(trimmed)) {
      const updated = [...customGenres, trimmed];
      setCustomGenres(updated);
      setNewGenreInput('');
      updatePreference('customGenres', updated);
    }
  };

  const removeGenre = (genre) => {
    const updated = customGenres.filter(g => g !== genre);
    setCustomGenres(updated);
    if (defaultGenre === genre) {
      setDefaultGenre('Irish');
      updatePreference('defaultGenre', 'Irish');
    }
    updatePreference('customGenres', updated);
  };

  const addType = () => {
    const trimmed = newTypeInput.trim();
    if (trimmed && !customTypes.includes(trimmed)) {
      const updated = [...customTypes, trimmed];
      setCustomTypes(updated);
      setNewTypeInput('');
      updatePreference('customTypes', updated);
    }
  };

const addTag = () => {
    const trimmed = normalizeTag(newTagInput);
    if (trimmed && !customTags.includes(trimmed)) {
      const updated = [...customTags, trimmed];

    setCustomTags(updated);
    setNewTagInput('');
    updatePreference('customTags', updated);
  }
};

const removeTag = (tag) => {
  const updated = customTags.filter(t => t !== tag);
  setCustomTags(updated);
  updatePreference('customTags', updated);
};

  const removeType = (type) => {
    const updated = customTypes.filter(t => t !== type);
    setCustomTypes(updated);
    if (defaultType === type) {
      setDefaultType('');
      updatePreference('defaultType', '');
    }
    updatePreference('customTypes', updated);
  };

  if (loading) return <p>Loading preferences...</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      <button className="big-button" onClick={onBack}>← Back</button>
    
      <h1>Settings</h1>

      <div style={{ marginBottom: '20px' }}>
        <label style={textStyle}>Default Genre: </label>
        <select style={inputStyle}
          value={defaultGenre}
          onChange={(e) => {
            setDefaultGenre(e.target.value);
            updatePreference('defaultGenre', e.target.value);
          }}
        >
          {customGenres.map((g, i) => (
            <option key={i} value={g}>{g}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={textStyle}>Default Type: </label>
        <select style={inputStyle}
          value={defaultType}
          onChange={(e) => {
            setDefaultType(e.target.value);
            updatePreference('defaultType', e.target.value);
          }}
        >
          {customTypes.map((t, i) => (
            <option key={i} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={textStyle}>Default Key: </label>
        <input style={inputStyle}
          value={defaultKey}
          onChange={(e) => {
            setDefaultKey(e.target.value);
            updatePreference('defaultKey', e.target.value);
          }}
          placeholder="e.g. D, Em"
        />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={textStyle}>Default Status: </label>
        <select style={inputStyle}
          value={defaultStatus}
          onChange={(e) => {
            setDefaultStatus(e.target.value);
            updatePreference('defaultStatus', e.target.value);
          }}
        >
          <option value="-">—</option>
          <option value="Need to Learn">Need to Learn</option>
          <option value="Learning">Learning</option>
          <option value="Learned">Learned</option>
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={textStyle}>Theme: </label>
        <select style={inputStyle}
          value={theme}
          onChange={(e) => {
            setTheme(e.target.value);
            updatePreference('theme', e.target.value);
          }}
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="sepia">Sepia</option>
          <option value="blueprint">Blueprint</option>
          <option value="custom">Custom</option>
        </select>

      </div>

        {theme === 'custom' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={textStyle}>Custom Background Color:</label>
            <input
              type="color"
              value={customColor}
              onChange={(e) => {
                const bg = e.target.value;
                setCustomColor(bg);
                localStorage.setItem('customBgColor', bg);
                updatePreference('customBgColor', bg);

                // Determine contrast text color using YIQ
                const yiq = (parseInt(bg.slice(1, 3), 16) * 299 +
                            parseInt(bg.slice(3, 5), 16) * 587 +
                            parseInt(bg.slice(5, 7), 16) * 114) / 1000;
                const textColor = yiq >= 128 ? '#000000' : '#ffffff';

                // Apply CSS vars
                document.documentElement.style.setProperty('--custom-bg', bg);
                document.documentElement.style.setProperty('--custom-text', textColor);
                document.documentElement.style.setProperty('--custom-button-bg', bg);
                document.documentElement.style.setProperty('--custom-button-text', textColor);
              }}
              style={{ width: '60px', height: '40px', border: 'none', cursor: 'pointer' }}
            />
          </div>
        )}


      <div style={{ marginBottom: '20px' }}>
        <label style={textStyle}>UI Size: </label>
        <select style={inputStyle}
          value={uiSize}
          onChange={(e) => {
            const newSize = e.target.value;
            setUiSize(newSize);
            updatePreference('uiSize', newSize);
            localStorage.setItem('uiSize', newSize);
          }}
        >

          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
      </div>

<div style={{ marginBottom: '20px' }}>
  <label style={textStyle}>Autosuggest from thesession.org? </label>
  <select style={inputStyle}
    value={enableAutoSuggest}
    onChange={(e) => {
      setEnableAutoSuggest(e.target.value);
      updatePreference('enableAutoSuggest', e.target.value);
    }}
  >
    <option value="yes">Yes</option>
    <option value="no">No</option>
  </select>
</div>

<h3>Preview Settings</h3>

<div style={{ marginBottom: '12px' }}>
  <label>
    <input
      type="checkbox"
      checked={showAbcInSets}
      onChange={() => {
        setShowAbcInSets(!showAbcInSets);
        updatePreference('showAbcInSets', !showAbcInSets);
      }}
    />
    {' '}Show ABC preview in Set page
  </label>
</div>

<div style={{ marginBottom: '12px' }}>
  <label>
    <input
      type="checkbox"
      checked={showLyricsInSets}
      onChange={() => {
        setShowLyricsInSets(!showLyricsInSets);
        updatePreference('showLyricsInSets', !showLyricsInSets);
      }}
    />
    {' '}Show lyrics in Set page
  </label>
</div>

<div style={{ marginBottom: '12px' }}>
  <label>
    <input
      type="checkbox"
      checked={showAbcInSearch}
      onChange={() => {
        setShowAbcInSearch(!showAbcInSearch);
        updatePreference('showAbcInSearch', !showAbcInSearch);
      }}
    />
    {' '}Show ABC preview in Search page
  </label>
</div>

<div style={{ marginBottom: '20px' }}>
  <label>
    <input
      type="checkbox"
      checked={showLyricsInSearch}
      onChange={() => {
        setShowLyricsInSearch(!showLyricsInSearch);
        updatePreference('showLyricsInSearch', !showLyricsInSearch);
      }}
    />
    {' '}Show lyrics in Search page
  </label>
</div>


      <h4>AllMyGenres</h4>

      {usedGenres.length > 0 && (
        <details style={{ margin: '4px 0', textAlign: 'center' }}>
          <summary style={{
            cursor: 'pointer',
            fontStyle: 'italic',
            margin: 0,
            padding: 0,
            color: '#555',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            Genres in use ({usedGenres.length})
          </summary>
          <div style={{ marginTop: '6px', fontSize: '0.95em', color: '#555' }}>
            {usedGenres.join(', ')}
          </div>
        </details>
      )}

      {customGenres.filter(genre => !usedGenres.includes(genre)).length > 0 && (
        <details style={{ margin: '4px 0', textAlign: 'center' }}>
          <summary style={{
            cursor: 'pointer',
            fontStyle: 'italic',
            margin: 0,
            padding: 0,
            color: '#555',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            Remove unused genres ({customGenres.filter(genre => !usedGenres.includes(genre)).length})
          </summary>
          <div style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '6px',
            marginTop: '6px'
          }}>
            {customGenres.filter(genre => !usedGenres.includes(genre)).map((genre, i) => (
              <div key={i} style={{
                backgroundColor: '#444',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85em',
                lineHeight: '1'
              }}>
                {genre}
                <button
                  onClick={() => removeGenre(genre)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    marginLeft: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                  aria-label={`Remove ${genre}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
        <input
          style={{ ...inputStyle, width: 'calc(100% - 50px)', marginRight: '5px' }}
          placeholder="Add new genre"
          value={newGenreInput}
          onChange={(e) => setNewGenreInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addGenre()}
        />
        <button style={inputStyle} onClick={addGenre}>Add</button>
      </div>



      <h4>AllMyTypes</h4>

      {usedTypes.length > 0 && (
        <details style={{ margin: '4px 0', textAlign: 'center' }}>
          <summary style={{
            cursor: 'pointer',
            fontStyle: 'italic',
            margin: 0,
            padding: 0,
            color: '#555',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            Types in use ({usedTypes.length})
          </summary>
          <div style={{ marginTop: '6px', fontSize: '0.95em', color: '#555' }}>
            {usedTypes.join(', ')}
          </div>
        </details>
      )}

      {customTypes.filter(type => !usedTypes.includes(type)).length > 0 && (
        <details style={{ margin: '4px 0', textAlign: 'center' }}>
          <summary style={{
            cursor: 'pointer',
            fontStyle: 'italic',
            margin: 0,
            padding: 0,
            color: '#555',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            Remove unused types ({customTypes.filter(type => !usedTypes.includes(type)).length})
          </summary>
          <div style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '6px',
            marginTop: '6px'
          }}>
            {customTypes.filter(type => !usedTypes.includes(type)).map((type, i) => (
              <div key={i} style={{
                backgroundColor: '#444',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85em',
                lineHeight: '1'
              }}>
                {type}
                <button
                  onClick={() => removeType(type)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    marginLeft: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                  aria-label={`Remove ${type}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
        <input
          style={{ ...inputStyle, width: 'calc(100% - 50px)', marginRight: '5px' }}
          placeholder="Add new type"
          value={newTypeInput}
          onChange={(e) => setNewTypeInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addType()}
        />
        <button style={inputStyle} onClick={addType}>Add</button>
      </div>




      <h4>AllMyTags</h4>

      {usedTags.length > 0 && (
        <details style={{ margin: '4px 0', textAlign: 'center' }}>
          <summary style={{
            cursor: 'pointer',
            fontStyle: 'italic',
            margin: 0,
            padding: 0,
            color: '#555',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            Tags in use ({usedTags.length})
          </summary>
          <div style={{ marginTop: '6px', fontSize: '0.95em', color: '#555' }}>
            {usedTags.join(', ')}
          </div>
        </details>
      )}

      {customTags.filter(tag => !usedTags.includes(tag)).length > 0 && (
        <details style={{ margin: '4px 0', textAlign: 'center' }}>
          <summary style={{
            cursor: 'pointer',
            fontStyle: 'italic',
            margin: 0,
            padding: 0,
            color: '#555',
            fontSize: '0.9em',
            lineHeight: '1.4'
          }}>
            Remove unused tags ({customTags.filter(tag => !usedTags.includes(tag)).length})
          </summary>
          <div style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '6px',
            marginTop: '6px'
          }}>
            {customTags.filter(tag => !usedTags.includes(tag)).map((tag, i) => (
              <div key={i} style={{
                backgroundColor: '#444',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '999px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.85em',
                lineHeight: '1'
              }}>
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    marginLeft: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                  aria-label={`Remove ${tag}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      <div style={{ maxWidth: '240px', margin: '6px auto 0' }}>
        <input
          style={{ ...inputStyle, width: 'calc(100% - 50px)', marginRight: '5px' }}
          placeholder="Add new tag"
          value={newTagInput}
          onChange={(e) => setNewTagInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && addTag()}
        />
        <button style={inputStyle} onClick={addTag}>Add</button>
      </div>




      <hr />
      <h3>Import from TheSession.org</h3>


      <small style={{ display: 'block', marginBottom: '6px', color: '#666' }}>
        Paste the ID number from the URL of your tunebook (e.g. https://thesession.org/members/USERID/tunebook, enter <strong>USERID</strong>).
      </small>
      <input style={{ ...inputStyle, width: "50%", marginBottom: "10px" }}
        placeholder="Paste thesession.org UserID here"
        value={sessionUserId}
        onChange={(e) => setSessionUserId(e.target.value)}
      />

 <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0px', marginBottom: '20px' }}>
  <button style={inputStyle} onClick={importAllFromSession} disabled={importing}>
    {importing ? 'Importing…' : "Import all tunes from this user's public tunebook"}
  </button>
</div>

  <hr />


<button onClick={downloadAllToLocal}>
  ⬇️ Download All for Offline Use
</button>

<button onClick={() => {
  if (window.confirm("Clear all offline data (Dexie)?")) {
    dexie.delete().then(() => {
      alert('Dexie cleared – reload to re-init.');
    });
  }
}}>
  Clear Offline Cache (Dev)
</button>

    </div>



    
  );

  
};


export default Settings;